import React, { useEffect, useState } from "react";
import { NavDropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faBarsStaggered,
} from "@fortawesome/free-solid-svg-icons";
import { supportedLanguages } from "../../language/supportedLanguages";

interface Props {}

const esgLinks: Record<string, Record<string, string>> = {
  de: {
    prices: "https://esg2go.org/angebot-preise/",
    partner: "https://esg2go.org/pros-partner/",
    about: "https://esg2go.org/",
    community: "https://esg2go.org/community/",
    transparency: "https://esg2go.org/transparenz/",
    datasecurity: "https://esg2go.org/datensicherheit/",
    team: "https://esg2go.org/team-geschichte/",
    blog: "https://esg2go.org/blog/",
    media: "https://esg2go.org/medienspiegel/",
    faq: "https://esg2go.org/faq/",
    enterData: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=de",
  },
  en: {
    prices: "https://esg2go.org/en/offer-and-prices/",
    partner: "https://esg2go.org/en/pros-partners/",
    about: "https://esg2go.org/en/about-us/",
    community: "https://esg2go.org/en/community/",
    transparency: "https://esg2go.org/en/foundation/",
    datasecurity: "https://esg2go.org/en/data-security/",
    team: "https://esg2go.org/en/team-and-advisory-board/",
    blog: "https://esg2go.org/en/news-from-esg2go/",
    media: "https://esg2go.org/en/media-review/",
    faq: "https://esg2go.org/en/faq/",
    enterData: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=en",
  },
  fr: {
    prices: "https://esg2go.org/fr/offre-prix/",
    partner: "https://esg2go.org/fr/pros-partenaires/",
    about: "https://esg2go.org/fr/a-propos-de-nous/",
    community: "https://esg2go.org/fr/community/",
    transparency: "https://esg2go.org/fr/fondation/",
    datasecurity: "https://esg2go.org/fr/securite-des-donnees/",
    team: "https://esg2go.org/fr/equipe-et-conseil-consultatif/",
    blog: "https://esg2go.org/fr/actualites-desg2go/",
    media: "https://esg2go.org/fr/revue-de-presse/",
    faq: "https://esg2go.org/fr/faq/",
    enterData: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=fr",
  },
  it: {
    prices: "https://esg2go.org/it/offerte-e-prezzi/",
    partner: "https://esg2go.org/it/professionisti-e-partner/",
    about: "https://esg2go.org/it/chi-siamo/",
    community: "https://esg2go.org/it/comunita/",
    transparency: "https://esg2go.org/it/trasparenza/",
    datasecurity: "https://esg2go.org/it/sicurezza-dei-dati/",
    team: "https://esg2go.org/it/squadra-e-storia/",
    blog: "https://esg2go.org/it/blog/",
    media: "https://esg2go.org/it/recensione-dei-media/",
    faq: "https://esg2go.org/it/faq/",
    enterData: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=it",
  },
};

const Navigation: React.FunctionComponent<Props> = ({}: Props) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [navIsOpen, setNavIsOpen] = useState<boolean>(false);

  useEffect(() => {
    const langParam = searchParams.get("lang") || searchParams.get("lang");
    changeLanguage(langParam);
  }, []);

  const changeLanguage = (lang: string | null) => {
    if (!lang || !supportedLanguages.includes(lang.toLowerCase())) {
      lang = "de";
    }
    i18n.changeLanguage(lang.toLowerCase());

    const params = new URLSearchParams(searchParams.toString());
    if (lang.toLowerCase() === "de") {
      params.delete("lang");
    } else {
      params.set("lang", lang.toLowerCase());
    }
    setSearchParams(params);
  };

  const getLanguageFlag = (language: string) => {
    return (
      "https://esg2go.org/wp-content/plugins/sitepress-multilingual-cms/res/flags/" +
      language +
      ".png"
    );
  };

  return (
    <nav className="navbar navbar-expand-lg fixed-top" id="main-nav">
      <div className="container-lg">
        <a target="_blank" href="https://esg2go.org/">
          <img
            alt="logo"
            className="logo"
            src="https://esg2go.org/wp-content/uploads/2022/02/esg2go_Logo-nobyline-250.jpg"
          />
        </a>
        <button
          className="navbar-toggler"
          onClick={() => setNavIsOpen(!navIsOpen)}
        >
          <FontAwesomeIcon icon={faBarsStaggered} />
        </button>
        <div
          className={"collapse navbar-collapse" + (navIsOpen ? " show" : "")}
          id="navbarSupportedContent"
        >
          <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
            <li className="nav-item">
              <a
                className="nav-link"
                aria-current="page"
                href={esgLinks[i18n.language]?.prices}
              >
                {t("layout.navigation.prices")}
              </a>
            </li>
            <li className="nav-item">
              <a
                className="nav-link"
                aria-current="page"
                href={esgLinks[i18n.language]?.partner}
              >
                {t("layout.navigation.partner")}
              </a>
            </li>
            <NavDropdown
              id=""
              className="float-end"
              title={
                <>
                  {t("layout.navigation.about")}{" "}
                  <FontAwesomeIcon icon={faChevronDown} />
                </>
              }
            >
              <NavDropdown.Item href={esgLinks[i18n.language]?.community}>
                {t("layout.navigation.community")}
              </NavDropdown.Item>
              <NavDropdown.Item href={esgLinks[i18n.language]?.transparency}>
                {t("layout.navigation.transparency")}
              </NavDropdown.Item>
              <NavDropdown.Item href={esgLinks[i18n.language]?.datasecurity}>
                {t("layout.navigation.datasecurity")}
              </NavDropdown.Item>
              <NavDropdown.Item href={esgLinks[i18n.language]?.team}>
                {t("layout.navigation.team")}
              </NavDropdown.Item>
              <NavDropdown.Item href={esgLinks[i18n.language]?.blog}>
                {t("layout.navigation.blog")}
              </NavDropdown.Item>
              <NavDropdown.Item href={esgLinks[i18n.language]?.media}>
                {t("layout.navigation.media")}
              </NavDropdown.Item>
              <NavDropdown.Item href={esgLinks[i18n.language]?.faq}>
                {t("layout.navigation.faq")}
              </NavDropdown.Item>
            </NavDropdown>
            <li className="nav-item highlighted">
              <a
                className="nav-link"
                aria-current="page"
                target="_blank"
                href={esgLinks[i18n.language]?.enterData}
              >
                {t("layout.navigation.enterData")}
              </a>
            </li>
            <NavDropdown
              id=""
              className="float-end"
              title={
                <>
                  <img
                    className="language-flags"
                    src={getLanguageFlag(i18n.language)}
                  />{" "}
                  {i18n.language.toUpperCase()}{" "}
                  <FontAwesomeIcon icon={faChevronDown} />
                </>
              }
              onSelect={(e) => changeLanguage(e)}
            >
              {i18n.language !== "de" ? (
                <NavDropdown.Item eventKey="de">
                  <img className="language-flags" src={getLanguageFlag("de")} />{" "}
                  DE
                </NavDropdown.Item>
              ) : null}
              {i18n.language !== "en" ? (
                <NavDropdown.Item eventKey="en">
                  <img className="language-flags" src={getLanguageFlag("en")} />{" "}
                  EN
                </NavDropdown.Item>
              ) : null}
              {i18n.language !== "fr" ? (
                <NavDropdown.Item eventKey="fr">
                  <img className="language-flags" src={getLanguageFlag("fr")} />{" "}
                  FR
                </NavDropdown.Item>
              ) : null}
              {i18n.language !== "it" ? (
                <NavDropdown.Item eventKey="it">
                  <img className="language-flags" src={getLanguageFlag("it")} />{" "}
                  IT
                </NavDropdown.Item>
              ) : null}
            </NavDropdown>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
